import { formatBuildingAddress } from "@/utils/formatBuildingAddress";
import type { OrgProjectOverview } from "@/stores/organizations/repository";

// Minimal building shape the filter needs — keeps this helper decoupled from
// the buildings-page row type.
export interface FilterableBuilding {
  id: string;
  name: string;
  address?: string;
  registry_code?: string | null;
}

export interface BuildingsFilterResult<T extends FilterableBuilding> {
  /** Buildings that match the query by name or by one of their projects. */
  visible: T[];
  /** Projects whose name matched — highlighted in the expansion panel. */
  matchedProjectIds: Set<number>;
  /** Buildings matched via a project — force-expanded so the match is visible. */
  autoExpandIds: Set<string>;
}

/**
 * Case-insensitive substring filter over buildings AND their projects. A
 * building stays visible when its own name matches or any of its projects'
 * names match; project matches auto-expand the building and are highlighted.
 * An empty query passes everything through untouched.
 */
export function filterBuildingsByQuery<T extends FilterableBuilding>(
  buildings: T[],
  projectsByBuilding: Map<string, OrgProjectOverview[]>,
  query: string
): BuildingsFilterResult<T> {
  const q = query.trim().toLowerCase();
  const matchedProjectIds = new Set<number>();
  const autoExpandIds = new Set<string>();
  if (!q) return { visible: buildings, matchedProjectIds, autoExpandIds };

  const visible: T[] = [];
  for (const building of buildings) {
    // The address as the list renders it, so a match is always one the user can see.
    const nameMatches = [
      building.name,
      building.address ? formatBuildingAddress(building.address) : undefined,
      building.registry_code,
    ].some((value) => value?.toLowerCase().includes(q));
    const matchingProjects = (projectsByBuilding.get(building.id) ?? []).filter((p) =>
      p.projectName.toLowerCase().includes(q)
    );
    if (!nameMatches && matchingProjects.length === 0) continue;
    visible.push(building);
    if (matchingProjects.length > 0) {
      autoExpandIds.add(building.id);
      for (const project of matchingProjects) matchedProjectIds.add(project.projectId);
    }
  }
  return { visible, matchedProjectIds, autoExpandIds };
}
